import React, { useCallback, useEffect, useRef, useState } from 'react';
import LyricScroller from './LyricScroller';
import {
  EFFECT_PRESETS,
  DEFAULT_PRESET,
  decodeFromUrl,
  concatBlocks,
  renderMix,
  encodeWav,
  estimateSyncMs,
  formatClock,
} from './audioEngine';
import karaokeService from '../../services/karaoke.service';
import ShareTake from './ShareTake';
import { scoreVocalBuffer } from './scoreBuffer.mjs';
import { getContour } from '../../services/singingRoom.service';
import { FrameTracker } from './frameTracker.mjs';
import { RunningScore } from './runningScore.mjs';
import BeatWave from './BeatWave';

const COUNT_IN_SEC = 4;
const WORKLET_URL = `${process.env.PUBLIC_URL || ''}/gs-recorder-worklet.js`;

const STAGES = {
  SETUP: 'setup',
  COUNTING: 'counting',
  RECORDING: 'recording',
  REVIEW: 'review',
};

export default function StudioRecorder({ song, currentUser, onSaved }) {
  const [stage, setStage] = useState(STAGES.SETUP);
  const [headphones, setHeadphones] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [countdown, setCountdown] = useState(COUNT_IN_SEC);
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [trackReady, setTrackReady] = useState(false);

  const [preset, setPreset] = useState(DEFAULT_PRESET);
  const [syncMs, setSyncMs] = useState(-60);
  const [vocalGain, setVocalGain] = useState(1);
  const [musicGain, setMusicGain] = useState(0.8);

  const [mixUrl, setMixUrl] = useState(null);
  // Same grade the singing room shows, from the same module — a take must not score
  // 91 here and 68 on stage.
  const [score, setScore] = useState(null);
  // Live standing while the take is running. Same formula as the final score —
  // both go through combineScore() — so the ticker settles onto the saved number
  // instead of contradicting it.
  const [live, setLive] = useState(null);
  const [contour, setContour] = useState(null);
  const [saved, setSaved] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [shared, setShared] = useState(false);

  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const workletRef = useRef(null);
  const musicSourceRef = useRef(null);
  const musicBufferRef = useRef(null);
  const blocksRef = useRef([]);
  const vocalBufferRef = useRef(null);
  const mixBufferRef = useRef(null);
  const startedAtRef = useRef(0);
  const rafRef = useRef(null);
  const previewRef = useRef(null);
  // The backing track's onended fires long after start() captured its scope, so
  // it has to reach the current stop() through a ref rather than a closure.
  const stopRef = useRef(null);
  const trackerRef = useRef(null);
  const runningRef = useRef(null);
  const contourRef = useRef(null);

  /* ------------------------------------------------------------ cleanup */

  const teardown = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { musicSourceRef.current && musicSourceRef.current.stop(); } catch (e) { /* already stopped */ }
    musicSourceRef.current = null;
    if (workletRef.current) {
      try { workletRef.current.port.postMessage('stop'); } catch (e) { /* closed */ }
      try { workletRef.current.disconnect(); } catch (e) { /* closed */ }
      workletRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    teardown();
    if (ctxRef.current && ctxRef.current.state !== 'closed') ctxRef.current.close();
    if (mixUrl) URL.revokeObjectURL(mixUrl);
  }, [teardown, mixUrl]);

  // Fetching and decoding the backing track on the record tap put ~13s between
  // the tap and the first note (measured: 16.9s to recording, of which only 4s
  // was the count-in). Do it when the studio opens instead — decodeAudioData
  // works fine on a context that is still suspended, so no gesture is needed.
  // Fetch the reference contour up front. It is 30-45KB of static integers and the
  // live meter cannot start without it; loading it on the record tap would put the
  // fetch inside the count-in.
  useEffect(() => {
    let cancelled = false;
    getContour(song.id)
      .then((c) => { if (!cancelled) { contourRef.current = c; setContour(c); } })
      .catch(() => { contourRef.current = null; });
    return () => { cancelled = true; };
  }, [song.id]);

  useEffect(() => {
    if (!song.instrumental_url) { setTrackReady(true); return undefined; }
    let cancelled = false;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return undefined;

    const ctx = ctxRef.current && ctxRef.current.state !== 'closed' ? ctxRef.current : new AudioCtx();
    ctxRef.current = ctx;
    setBusy('Loading the backing track…');
    decodeFromUrl(ctx, song.instrumental_url)
      .then((buffer) => {
        if (cancelled) return;
        musicBufferRef.current = buffer;
        setTrackReady(true);
        setBusy(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setBusy(null);
        setTrackReady(true); // let them record a cappella rather than blocking
        setError(`Could not load the backing track (${e.message}). You can still record without it.`);
      });
    return () => { cancelled = true; };
  }, [song.instrumental_url]);

  /* ---------------------------------------------------------- recording */

  const start = async () => {
    setError(null);
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) throw new Error('This browser has no Web Audio support');

      const ctx = ctxRef.current && ctxRef.current.state !== 'closed' ? ctxRef.current : new AudioCtx();
      ctxRef.current = ctx;
      // iOS starts contexts suspended; this call must stay inside the tap.
      if (ctx.state === 'suspended') await ctx.resume();

      if (!ctx.audioWorklet) throw new Error('This browser cannot record here — try Chrome or Safari 15+');
      await ctx.audioWorklet.addModule(WORKLET_URL);

      // On headphones we want the voice untouched. On speakers the backing
      // track bleeds into the mic, so the browser's echo canceller has to stay
      // on even though it dulls the vocal.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: headphones
          ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false }
          : { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      if (song.instrumental_url && !musicBufferRef.current) {
        setBusy('Loading the backing track…');
        musicBufferRef.current = await decodeFromUrl(ctx, song.instrumental_url);
        setBusy(null);
      }

      blocksRef.current = [];
      // One capture, two consumers: the raw blocks still build the mix, and the same
      // blocks feed the tracker so the singer sees a score as they go. Tapping the
      // mic a second time for scoring would prompt again and, on some phones, hand
      // back differently-processed audio.
      trackerRef.current = new FrameTracker(ctx.sampleRate);
      runningRef.current = contourRef.current ? new RunningScore(contourRef.current) : null;
      setLive(null);

      const worklet = new AudioWorkletNode(ctx, 'gs-recorder', { numberOfOutputs: 1 });
      worklet.port.onmessage = (event) => {
        const block = event.data;
        blocksRef.current.push(block);
        let peak = 0;
        for (let i = 0; i < block.length; i += 16) peak = Math.max(peak, Math.abs(block[i]));
        setLevel(peak);

        // Frames are timed from the start of capture; the song starts COUNT_IN_SEC
        // later, so shift them onto song time before scoring.
        const running = runningRef.current;
        if (!running || !trackerRef.current) return;
        for (const f of trackerRef.current.push(block)) {
          const songT = f.t - COUNT_IN_SEC;
          if (songT >= 0) running.push({ ...f, t: songT });
        }
      };
      workletRef.current = worklet;

      const mic = ctx.createMediaStreamSource(stream);
      const sink = ctx.createGain();
      sink.gain.value = 0; // pulls the worklet without monitoring into the room
      mic.connect(worklet).connect(sink).connect(ctx.destination);

      setSyncMs(estimateSyncMs(ctx));

      const t0 = ctx.currentTime + 0.15;
      startedAtRef.current = t0;
      worklet.port.postMessage('start');

      if (musicBufferRef.current) {
        const source = ctx.createBufferSource();
        source.buffer = musicBufferRef.current;
        source.connect(ctx.destination);
        source.start(t0 + COUNT_IN_SEC);
        source.onended = () => { if (stopRef.current) stopRef.current(); };
        musicSourceRef.current = source;
      }

      setStage(STAGES.COUNTING);
      setCountdown(COUNT_IN_SEC);
      setElapsed(0);

      const tick = () => {
        const ctxNow = ctxRef.current;
        if (!ctxNow) return;
        const since = ctxNow.currentTime - startedAtRef.current;
        if (since < COUNT_IN_SEC) {
          setCountdown(Math.max(1, Math.ceil(COUNT_IN_SEC - since)));
          setStage(STAGES.COUNTING);
        } else {
          setStage(STAGES.RECORDING);
          setElapsed(since - COUNT_IN_SEC);
          if (runningRef.current) setLive(runningRef.current.read());
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      setBusy(null);
      setError(
        e.name === 'NotAllowedError'
          ? 'Microphone blocked. Allow mic access in your browser settings and try again.'
          : e.message
      );
      teardown();
      setStage(STAGES.SETUP);
    }
  };

  // Nothing in here may throw silently. Every failure used to reject this
  // promise unhandled, which left the take running and the button still saying
  // "Stop" with no clue why — and, because the effects only exist in the review
  // step, no way to reach them either.
  const stop = useCallback(async () => {
    if (!workletRef.current || finishing) return; // already stopping
    setFinishing(true);
    setError(null);
    try {
      workletRef.current.port.postMessage('stop');
      // Let the final block land before assembling.
      await new Promise((resolve) => setTimeout(resolve, 150));
      teardown();

      const ctx = ctxRef.current;
      const blocks = blocksRef.current;
      if (!ctx) throw new Error('The audio engine closed before the take could be saved.');
      if (!blocks.length) {
        throw new Error('Nothing was recorded — check that the right microphone is selected and that this page has mic permission.');
      }

      vocalBufferRef.current = concatBlocks(blocks, ctx.sampleRate, ctx);
      setStage(STAGES.REVIEW);
      setLevel(0);

      // Score against the song's reference contour. A song with no contour simply
      // does not get a grade — better than inventing one. Scoring failure must never
      // cost the singer their take, so it is deliberately outside the mix's try.
      try {
        const contour = await getContour(song.id);
        setScore(contour
          ? scoreVocalBuffer({
            contour,
            buffer: vocalBufferRef.current,
            leadSec: COUNT_IN_SEC,
          })
          : null);
      } catch (e) {
        console.error('scoring failed:', e.message);
        setScore(null);
      }

      await buildMix({ presetKey: preset, sync: syncMs, vGain: vocalGain, mGain: musicGain });
    } catch (e) {
      setError(`${e.message || 'Could not finish the take'} — nothing was lost on our side, please try again.`);
      teardown();
      setStage(STAGES.SETUP);
    } finally {
      setFinishing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teardown, finishing, preset, syncMs, vocalGain, musicGain]);

  useEffect(() => { stopRef.current = stop; }, [stop]);

  /* ---------------------------------------------------------- the mixing */

  const buildMix = async ({ presetKey, sync, vGain, mGain }) => {
    if (!vocalBufferRef.current) return;
    setBusy('Mixing…');
    setError(null);
    try {
      const buffer = await renderMix({
        vocalBuffer: vocalBufferRef.current,
        musicBuffer: musicBufferRef.current,
        musicLeadSec: COUNT_IN_SEC,
        syncMs: sync,
        presetKey,
        vocalGain: vGain,
        musicGain: mGain,
      });
      mixBufferRef.current = buffer;
      const blob = encodeWav(buffer);
      if (mixUrl) URL.revokeObjectURL(mixUrl);
      setMixUrl(URL.createObjectURL(blob));
    } catch (e) {
      setError(e.message);
    }
    setBusy(null);
  };

  const remix = () => buildMix({ presetKey: preset, sync: syncMs, vGain: vocalGain, mGain: musicGain });

  const retake = () => {
    if (previewRef.current) previewRef.current.pause();
    if (mixUrl) URL.revokeObjectURL(mixUrl);
    setMixUrl(null);
    setScore(null);
    setSaved(null);
    setVideoUrl(null);
    mixBufferRef.current = null;
    vocalBufferRef.current = null;
    blocksRef.current = [];
    setStage(STAGES.SETUP);
  };

  /* ----------------------------------------------------------- publishing */

  const save = async () => {
    if (!mixBufferRef.current) return;
    setBusy('Uploading your take…');
    setError(null);
    try {
      const blob = encodeWav(mixBufferRef.current);
      const result = await karaokeService.saveTake({
        blob,
        songId: song.id,
        title: `${song.title} — ${currentUser?.name || 'GeetSuhane'}`,
        effect: preset,
        syncMs,
        vocalGain,
        musicGain,
        duration: mixBufferRef.current.duration,
        visibility: 'public',
      });
      setSaved(result);
      if (onSaved) onSaved(result);

      setBusy('Making your video…');
      const video = await karaokeService.renderVideo(result.id);
      setVideoUrl(video.video_url);
    } catch (e) {
      setError(e.message);
    }
    setBusy(null);
  };

  const share = async () => {
    if (!saved) return;
    setBusy('Sharing to the feed…');
    setError(null);
    try {
      await karaokeService.shareTake(saved.id);
      setShared(true);
    } catch (e) {
      setError(e.message);
    }
    setBusy(null);
  };

  /* --------------------------------------------------------------- render */

  const meterWidth = `${Math.min(100, Math.round(level * 140))}%`;
  const recordingTime = stage === STAGES.RECORDING ? elapsed : 0;

  return (
    <div className="gs-studio">
      {error && <div className="gs-studio__error">{error}</div>}
      {busy && <div className="gs-studio__busy">{busy}</div>}

      {stage === STAGES.SETUP && (
        <div className="gs-studio__setup">
          <label className="gs-studio__check">
            <input
              type="checkbox"
              checked={headphones}
              onChange={(e) => setHeadphones(e.target.checked)}
            />
            <span>
              <strong>I'm wearing headphones</strong>
              <small>
                On speakers the backing track leaks into your mic and the browser has to
                fight it — your voice comes out thin and choppy. Headphones fix it.
              </small>
            </span>
          </label>

          <div className="gs-studio__panel">
            <h5>Room</h5>
            <div className="gs-studio__presets">
              {Object.entries(EFFECT_PRESETS).map(([key, value]) => (
                <button
                  type="button"
                  key={key}
                  className={key === preset ? 'gs-chip is-active' : 'gs-chip'}
                  onClick={() => setPreset(key)}
                >
                  {value.label}
                </button>
              ))}
            </div>
            <p className="gs-studio__hint">
              {EFFECT_PRESETS[preset].hint} You can change this after recording too —
              the effect is applied when your take is mixed, not while you sing.
            </p>
          </div>

          {!song.instrumental_url && (
            <p className="gs-studio__note">
              This song has no backing track yet, so you'll be recording a cappella.
            </p>
          )}

          <button
            type="button"
            className="gs-btn gs-btn--record"
            onClick={start}
            disabled={!trackReady}
          >
            {trackReady ? '● Start recording' : 'Loading the backing track…'}
          </button>
          <p className="gs-studio__hint">
            {COUNT_IN_SEC} second count-in, then the music starts. Everything is mixed on
            your own device — nothing is uploaded until you choose to save.
          </p>
        </div>
      )}

      {(stage === STAGES.COUNTING || stage === STAGES.RECORDING) && (
        <div className="gs-studio__live">
          {stage === STAGES.COUNTING ? (
            <div className="gs-studio__count">{countdown}</div>
          ) : (
            <div className="gs-studio__timer">
              <span className="gs-studio__dot" /> {formatClock(recordingTime)}
            </div>
          )}

          <div className="gs-studio__meter">
            <div className="gs-studio__meter-fill" style={{ width: meterWidth }} />
          </div>

          {/* Live scoring. Only rendered when the song actually has a reference
              contour — a song without one can still be sung, it just cannot be
              scored, and showing an empty meter would imply otherwise. */}
          {contour && stage === STAGES.RECORDING && (
            <div className="gs-live">
              <div className="gs-live__top">
                <div className={`gs-live__points is-${(live?.grade || 'C').replace('+', 'plus')}`}>
                  <span className="gs-live__num">{live?.score ?? 0}</span>
                  <span className="gs-live__of">points</span>
                </div>
                <div className={`gs-live__grade is-${(live?.grade || 'C').replace('+', 'plus')}`}>
                  {live?.grade || '—'}
                </div>
              </div>

              <BeatWave contour={contour} songTime={recordingTime} reading={live} height={104} />

              <div className="gs-live__bars">
                {[
                  ['Pitch', live?.pitch],
                  ['Timing', live?.timing],
                  ['Sung', live?.completeness],
                ].map(([label, value]) => (
                  <div className="gs-live__bar" key={label}>
                    <span>{label}</span>
                    <div className="gs-live__track">
                      <div
                        className="gs-live__fill"
                        style={{ width: `${Math.round((value || 0) * 100)}%` }}
                      />
                    </div>
                    <em>{Math.round((value || 0) * 100)}%</em>
                  </div>
                ))}
              </div>

              {live && !live.settled && (
                <p className="gs-live__note">Measuring your mic delay…</p>
              )}
            </div>
          )}

          <LyricScroller lrc={song.lrc} plain={song.lyrics} time={recordingTime} compact />

          <button
            type="button"
            className="gs-btn gs-btn--stop"
            onClick={stop}
            disabled={finishing}
          >
            {finishing ? 'Finishing your take…' : '■ Stop'}
          </button>
        </div>
      )}

      {stage === STAGES.REVIEW && (
        <div className="gs-studio__review">
          {score && (
            <div className={`gs-singroom__score is-${score.grade.replace('+', 'plus')}`}>
              <span className="gs-singroom__grade">{score.grade}</span>
              <strong>{score.score}/100</strong>
              {score.reason
                ? <em>{score.reason}</em>
                : (
                  <em>
                    pitch {Math.round(score.pitch * 100)}% · timing {Math.round(score.timing * 100)}%
                    {' '}· sang {Math.round(score.completeness * 100)}% of the song
                  </em>
                )}
            </div>
          )}

          {mixUrl && (
            <audio ref={previewRef} className="gs-studio__player" src={mixUrl} controls />
          )}

          <div className="gs-studio__panel">
            <h5>Room</h5>
            <div className="gs-studio__presets">
              {Object.entries(EFFECT_PRESETS).map(([key, value]) => (
                <button
                  type="button"
                  key={key}
                  className={key === preset ? 'gs-chip is-active' : 'gs-chip'}
                  onClick={() => setPreset(key)}
                >
                  {value.label}
                </button>
              ))}
            </div>
            <p className="gs-studio__hint">{EFFECT_PRESETS[preset].hint}</p>
          </div>

          <div className="gs-studio__panel">
            <h5>
              Timing <span className="gs-studio__value">{syncMs > 0 ? `+${syncMs}` : syncMs} ms</span>
            </h5>
            <input
              type="range"
              min={-400}
              max={400}
              step={10}
              value={syncMs}
              onChange={(e) => setSyncMs(parseInt(e.target.value, 10))}
            />
            <p className="gs-studio__hint">
              Drag left if your voice sounds late, right if it sounds early. Browsers add a
              delay between what you hear and what gets recorded, so a raw take almost
              always needs a nudge left.
            </p>
          </div>

          <div className="gs-studio__panel gs-studio__panel--split">
            <div>
              <h5>Voice <span className="gs-studio__value">{Math.round(vocalGain * 100)}%</span></h5>
              <input
                type="range" min={0.2} max={2} step={0.05}
                value={vocalGain}
                onChange={(e) => setVocalGain(parseFloat(e.target.value))}
              />
            </div>
            <div>
              <h5>Music <span className="gs-studio__value">{Math.round(musicGain * 100)}%</span></h5>
              <input
                type="range" min={0} max={1.5} step={0.05}
                value={musicGain}
                onChange={(e) => setMusicGain(parseFloat(e.target.value))}
              />
            </div>
          </div>

          <div className="gs-studio__actions">
            <button type="button" className="gs-btn gs-btn--ghost" onClick={remix} disabled={!!busy}>
              Apply changes
            </button>
            <button type="button" className="gs-btn gs-btn--ghost" onClick={retake} disabled={!!busy}>
              Record again
            </button>
            {mixUrl && (
              <a className="gs-btn gs-btn--ghost" href={mixUrl} download={`${song.title}.wav`}>
                Download audio
              </a>
            )}
            {!saved && (
              <button type="button" className="gs-btn gs-btn--record" onClick={save} disabled={!!busy}>
                Save &amp; make video
              </button>
            )}
          </div>

          {videoUrl && (
            <div className="gs-studio__done">
              <h5>Your video is ready</h5>
              <a className="gs-btn gs-btn--record" href={videoUrl} download>
                Download MP4
              </a>
              <ShareTake
                videoUrl={videoUrl}
                title={`${song.title} — cover by ${currentUser?.name || 'a GeetSuhane singer'}`}
                description={
                  `${song.title} — cover by ${currentUser?.name || 'a GeetSuhane singer'}\n\n` +
                  `Original song: ${song.credit || 'GeetSuhane Originals'}` +
                  `${song.youtube_id ? `\nOriginal: https://youtu.be/${song.youtube_id}` : ''}\n` +
                  `Recorded on GeetSuhane — https://geetsuhane.com/fs`
                }
              />
              <button
                type="button"
                className="gs-btn gs-btn--ghost gs-studio__share"
                onClick={share}
                disabled={!!busy || shared}
              >
                {shared ? 'Shared to the feed ✓' : 'Also share to the GeetSuhane feed'}
              </button>
              <p className="gs-studio__hint">
                Post it to your own channel. Suggested description:
              </p>
              <textarea
                className="gs-studio__copy"
                readOnly
                rows={4}
                value={
                  `${song.title} — cover by ${currentUser?.name || 'a GeetSuhane singer'}\n\n` +
                  `Original song: ${song.credit || 'GeetSuhane Originals'}` +
                  `${song.youtube_id ? `\nOriginal: https://youtu.be/${song.youtube_id}` : ''}\n` +
                  `Recorded on GeetSuhane — https://geetsuhane.com/fs`
                }
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
